// ── Pure helper functions extracted from stateMachine.ts ────────────
// These are standalone functions with zero class dependency.

import { doomClockCapForLevel } from "./levelConfig";
import { chainMultiplierForDepth, GROOVE_MODE_MULTIPLIER } from "./scoring";
import { clamp01 } from "../shared/easing";
import { STATE_MACHINE_TUNING } from "./tuning";
import {
  createObjectiveStateForLevel,
  emptyObjectiveTelemetry,
  normalizeObjectiveState,
} from "./progression";
import type {
  CollapseAnimStep,
  LevelObjectiveState,
  MatchTier,
  ObjectiveTelemetry,
  Position,
  PowerupKind,
  ResolveResult,
  ResolveScoreEvent,
  RotationAnimPhase,
  RunState,
} from "./types";
import type { BombHapticType } from "./haptics";

// ── Cash / scoring helpers ──────────────────────────────────────────

export const CASH_MATCH_PAYOUT = {
  line3: 3,
  line4: 4,
  line5Plus: 5,
  square: 4,
  composite: 8,
  lShape: 7,
  tShape: 10
} as const;

export const CASH_POWERUP_COMPLETION_PAYOUT = 10;

export function cashForScoreEvent(event: ResolveScoreEvent): number {
  if (event.cashAmount != null) {
    return event.cashAmount;
  }
  if (event.cashEligible !== true) {
    return 0;
  }
  if (event.shape === "T") {
    return CASH_MATCH_PAYOUT.tShape;
  }
  if (event.shape === "L") {
    return CASH_MATCH_PAYOUT.lShape;
  }
  if (event.shape === "SQUARE") {
    return CASH_MATCH_PAYOUT.square;
  }
  if (event.shape === "COMPOSITE") {
    return CASH_MATCH_PAYOUT.composite;
  }
  if (event.shape !== "LINE") {
    return 0;
  }
  if (event.matchLen >= 5) {
    return CASH_MATCH_PAYOUT.line5Plus;
  }
  if (event.matchLen === 4) {
    return CASH_MATCH_PAYOUT.line4;
  }
  if (event.matchLen === 3) {
    return CASH_MATCH_PAYOUT.line3;
  }
  return 0;
}

export function completionCashForPowerup(sourcePowerup?: PowerupKind): number {
  if (sourcePowerup === "ROWRIP" || sourcePowerup === "COLCUT") {
    return CASH_POWERUP_COMPLETION_PAYOUT;
  }
  if (sourcePowerup === "ZIGZAGBOLT") {
    return 20;
  }
  return 0;
}

export interface QueuedScoreBatchContext {
  grooveMultiplier: number;
  resolveRawPointsBeforeBatch: number;
}

export function resolveBatchPoints(
  rawPoints: number,
  chainDepth: number,
  context: QueuedScoreBatchContext
): number {
  const scaledMultiplier = chainMultiplierForDepth(chainDepth) * context.grooveMultiplier;
  const rawPointsBeforeBatch = Math.max(0, context.resolveRawPointsBeforeBatch);
  const rawPointsThroughBatch = rawPointsBeforeBatch + Math.max(0, rawPoints);
  return Math.round(rawPointsThroughBatch * scaledMultiplier) - Math.round(rawPointsBeforeBatch * scaledMultiplier);
}

// ── Doom clock ──────────────────────────────────────────────────────

export function clampDoomClockSecForLevel(level: number, doomClockSec: number): number {
  return Math.max(0, Math.min(doomClockCapForLevel(level), doomClockSec));
}

// ── Animation timing ────────────────────────────────────────────────

export function phaseDuration(phase: RotationAnimPhase, durationMultiplier = 1): number {
  if (phase === "grid_spin") {
    return STATE_MACHINE_TUNING.rotationAnimation.gridSpinSec * durationMultiplier;
  }
  if (phase === "sideways_hold") {
    return STATE_MACHINE_TUNING.rotationAnimation.sidewaysHoldSec * durationMultiplier;
  }
  return STATE_MACHINE_TUNING.rotationAnimation.tileRecoverSec * durationMultiplier;
}

export function collapseDurationForTier(tier: MatchTier): number {
  if (tier === 3) {
    return STATE_MACHINE_TUNING.collapseAnimation.tier3Sec;
  }
  if (tier === 4) {
    return STATE_MACHINE_TUNING.collapseAnimation.tier4Sec;
  }
  return STATE_MACHINE_TUNING.collapseAnimation.tier5Sec;
}

export function collapseDurationForStep(step: CollapseAnimStep, durationMultiplier = 1): number {
  let duration = step.durationOverrideSec ?? collapseDurationForTier(step.tier);
  if (step.specialFx === "BOMB_EXPLOSION") {
    duration *= 1.5;
  }
  return duration * durationMultiplier;
}

// ── Collapse / resolve helpers ──────────────────────────────────────

export function hasDirectHitWipeOverlay(step: CollapseAnimStep): boolean {
  return step.clearedTileOverlays?.some((overlay) => overlay.tile.special === "WIPE") === true;
}

export function interruptedLinearPowerupCompletion(out: ResolveResult, sourcePowerup?: PowerupKind): boolean {
  if (sourcePowerup === "ROWRIP") {
    return out.visualSteps.some((step) => step.specialFx === "POWERUP_ROW_SWEEP" && hasDirectHitWipeOverlay(step));
  }
  if (sourcePowerup === "COLCUT") {
    return out.visualSteps.some((step) => step.specialFx === "POWERUP_COL_SWEEP" && hasDirectHitWipeOverlay(step));
  }
  return false;
}

// ── Haptics helpers ─────────────────────────────────────────────────

export function strongerBomb(
  current: ResolveResult["haptics"]["strongestBomb"],
  candidate: ResolveResult["haptics"]["strongestBomb"]
): ResolveResult["haptics"]["strongestBomb"] {
  if (candidate == null) {
    return current;
  }
  if (current == null) {
    return candidate;
  }
  if (candidate === "BOMB_5" || current === "BOMB_3") {
    return candidate;
  }
  if (candidate === "BOMB_4" && current !== "BOMB_5") {
    return candidate;
  }
  return current;
}

export function toBombHapticType(bomb: ResolveResult["haptics"]["strongestBomb"]): BombHapticType | null {
  if (bomb == null) {
    return null;
  }
  if (bomb === "BOMB_3") {
    return "bomb_3";
  }
  if (bomb === "BOMB_4") {
    return "bomb_4";
  }
  return "bomb_5";
}

export function toBombHapticTypeFromSpecial(special: "BOMB_3" | "BOMB_4" | "BOMB_5"): BombHapticType {
  if (special === "BOMB_3") {
    return "bomb_3";
  }
  if (special === "BOMB_4") {
    return "bomb_4";
  }
  return "bomb_5";
}

export function strongestBombSeedHapticType(
  seeds: Array<{ special: "BOMB_3" | "BOMB_4" | "BOMB_5" }>
): BombHapticType | null {
  if (seeds.length === 0) {
    return null;
  }
  let strongest: BombHapticType = "bomb_3";
  for (const seed of seeds) {
    const next = toBombHapticTypeFromSpecial(seed.special);
    if ((next === "bomb_5" && strongest !== "bomb_5")
      || (next === "bomb_4" && strongest === "bomb_3")) {
      strongest = next;
    }
  }
  return strongest;
}

// ── Normalization helpers ───────────────────────────────────────────

export function normalizeGrooveState(groove: Partial<RunState["groove"]> | null | undefined): RunState["groove"] {
  const fill = clamp01(groove?.groove ?? 0);
  const active = groove?.active === true && (groove?.activeRemainingSec ?? 0) > 0;
  const readyWindowSec = Math.max(0, groove?.readyWindowSec ?? 0);
  const armed = active
    ? false
    : groove?.armed === true && fill >= 1 && readyWindowSec > 0;
  const freeRotationsRemaining = Math.max(0, Math.floor(groove?.freeRotationsRemaining ?? 0));
  return {
    groove: active || armed ? 1 : fill,
    multiplier: active ? GROOVE_MODE_MULTIPLIER : 1,
    armed,
    readyWindowSec: armed ? readyWindowSec : 0,
    active,
    activeRemainingSec: active ? Math.max(0, groove?.activeRemainingSec ?? 0) : 0,
    freeRotationsRemaining: active ? freeRotationsRemaining : 0
  };
}

export function normalizePowerupInventory(
  inventory: Partial<RunState["powerupInventory"]> | null | undefined
): RunState["powerupInventory"] {
  return {
    HINT: Math.max(0, Math.floor(inventory?.HINT ?? 0)),
    ROWRIP: Math.max(0, Math.floor(inventory?.ROWRIP ?? 0)),
    COLCUT: Math.max(0, Math.floor(inventory?.COLCUT ?? 0)),
    TRANSMUTE: Math.max(0, Math.floor(inventory?.TRANSMUTE ?? 0)),
    ZIGZAGBOLT: Math.max(0, Math.floor(inventory?.ZIGZAGBOLT ?? 0))
  };
}

export function normalizeLevel20BountyCompletions(value: number | null | undefined): number {
  return Math.max(0, Math.floor(value ?? 0));
}

export function normalizeLevelAdvanceBaseScore(levelScore: number, value: number | null | undefined): number {
  const normalizedLevelScore = typeof levelScore === "number" && Number.isFinite(levelScore)
    ? Math.max(0, Math.floor(levelScore))
    : 0;
  const normalizedValue = typeof value === "number" && Number.isFinite(value)
    ? Math.floor(value)
    : 0;
  return Math.max(0, Math.min(normalizedLevelScore, normalizedValue));
}

export function normalizeObjectiveStateForLevel(
  level: number,
  state: Partial<LevelObjectiveState> | null | undefined
): LevelObjectiveState | null {
  const fallback = createObjectiveStateForLevel(level);
  if (fallback == null) {
    return null;
  }
  if (state?.kind !== fallback.kind) {
    return fallback;
  }
  return normalizeObjectiveState(fallback.kind, fallback.target, state);
}

// ── Telemetry helpers ───────────────────────────────────────────────

export function mergeObjectiveTelemetry(
  base: ObjectiveTelemetry | null | undefined,
  next: ObjectiveTelemetry | null | undefined
): ObjectiveTelemetry {
  const left = base ?? emptyObjectiveTelemetry();
  const right = next ?? emptyObjectiveTelemetry();
  return {
    line4PlusMatches: left.line4PlusMatches + right.line4PlusMatches,
    line5PlusMatches: left.line5PlusMatches + right.line5PlusMatches,
    shapeMatches: left.shapeMatches + right.shapeMatches,
    squareMatches: left.squareMatches + right.squareMatches,
    lMatches: left.lMatches + right.lMatches,
    tMatches: left.tMatches + right.tMatches,
    specialsCreated: left.specialsCreated + right.specialsCreated,
    bombTriggers: left.bombTriggers + right.bombTriggers,
    wipeTriggers: left.wipeTriggers + right.wipeTriggers,
    specialTriggers: left.specialTriggers + right.specialTriggers,
    crateBreaks: left.crateBreaks + right.crateBreaks,
    obstacleClears: left.obstacleClears + right.obstacleClears,
    specialClearTiles: left.specialClearTiles + right.specialClearTiles,
    wipeClearTiles: left.wipeClearTiles + right.wipeClearTiles,
    chainDepth2PlusResolves: left.chainDepth2PlusResolves + right.chainDepth2PlusResolves,
    chainDepth3PlusResolves: left.chainDepth3PlusResolves + right.chainDepth3PlusResolves,
    chainDepth4PlusResolves: left.chainDepth4PlusResolves + right.chainDepth4PlusResolves
  };
}

export function hasObjectiveTelemetryValue(telemetry: ObjectiveTelemetry | null | undefined): boolean {
  if (telemetry == null) {
    return false;
  }
  return telemetry.line4PlusMatches > 0
    || telemetry.line5PlusMatches > 0
    || telemetry.shapeMatches > 0
    || telemetry.specialsCreated > 0
    || telemetry.bombTriggers > 0
    || telemetry.wipeTriggers > 0
    || telemetry.specialTriggers > 0
    || telemetry.crateBreaks > 0
    || telemetry.obstacleClears > 0
    || telemetry.specialClearTiles > 0
    || telemetry.wipeClearTiles > 0
    || telemetry.chainDepth2PlusResolves > 0
    || telemetry.chainDepth3PlusResolves > 0
    || telemetry.chainDepth4PlusResolves > 0;
}

export function mergeUniquePositions(a: Position[], b: Position[]): Position[] {
  const out = new Map<string, Position>();
  for (const p of a) {
    out.set(`${p.row},${p.col}`, p);
  }
  for (const p of b) {
    out.set(`${p.row},${p.col}`, p);
  }
  return [...out.values()];
}

// ── Pending score batch interface ───────────────────────────────────

export interface PendingScoreBatch {
  points: number;
  chainDepth: number;
  chainIndex: number;
  scoreEvents: ResolveScoreEvent[];
  commitMode: "immediate" | "deferred_combo";
  context: QueuedScoreBatchContext;
}
